import React from 'react';
import { Link } from 'react-router-dom';
import MetaTags from '../../components/seo/MetaTags';

export default function TermsOfService() {
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-900 pt-20">
      <MetaTags
        title="Terms of Service | DiversiCoin Bitcoin BTM Network"
        description="Terms and conditions for using DiversiCoin Bitcoin BTMs and related services."
        url="https://www.DiversiCoin.cash/terms-of-service"
      />
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-gray-800 rounded-lg p-8">
          <h1 className="text-3xl font-bold text-white mb-6">Terms of Service</h1>
          <div className="prose prose-invert">
            <p className="text-gray-300">Last updated: February 28, 2024</p>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">1. Definitions</h2>
              <ul className="list-disc pl-6 text-gray-300 mt-2">
                <li>"BTM" refers to Bitcoin Teller Machine operated by DiversiCoin</li>
                <li>"Transaction" means any purchase or sale of Bitcoin using our BTMs</li>
                <li>"KYC" means Know Your Customer verification procedures</li>
                <li>"Wallet" refers to your Bitcoin digital wallet</li>
                <li>"Host Business" means establishments where our BTMs are located</li>
                <li>"Network Fee" means Bitcoin blockchain transaction fees</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">2. Service Terms</h2>
              <ul className="list-disc pl-6 text-gray-300 mt-2">
                <li>You must be 18 years or older to use our BTMs</li>
                <li>Valid government-issued photo ID required for all transactions</li>
                <li>Transaction limits and verification requirements apply</li>
                <li>All fees are clearly displayed at the BTM before transaction</li>
                <li>By using our BTMs, you agree to these terms</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">3. User Responsibilities</h2>
              <ul className="list-disc pl-6 text-gray-300 mt-2">
                <li>Provide accurate and current identification information</li>
                <li>Maintain the security of your Bitcoin wallet and private keys</li>
                <li>Verify all transaction details before confirmation</li>
                <li>Report any suspicious activities immediately</li>
                <li>Follow all posted BTM instructions and safety guidelines</li>
                <li>Keep your transaction receipts for your records</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">4. Prohibited Activities</h2>
              <ul className="list-disc pl-6 text-gray-300 mt-2">
                <li>Using false or fraudulent identification</li>
                <li>Attempting to exceed transaction limits</li>
                <li>Tampering with BTM hardware or software</li>
                <li>Using BTMs for money laundering or illegal activities</li>
                <li>Structuring transactions to avoid reporting requirements</li>
                <li>Sharing or selling personal verification credentials</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">5. Fees and Limits</h2>
              <ul className="list-disc pl-6 text-gray-300 mt-2">
                <li>Service fees vary based on transaction type and amount</li>
                <li>Daily limits: $20 minimum to $10,000 maximum</li>
                <li>Network fees are included in displayed price</li>
                <li>All transactions are final and non-refundable</li>
                <li>Current rates and fees are displayed at BTM</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">6. Service Availability</h2>
              <ul className="list-disc pl-6 text-gray-300 mt-2">
                <li>BTMs are generally accessible 24/7</li>
                <li>Service may be interrupted for maintenance</li>
                <li>Network disruptions may affect transaction times</li>
                <li>We reserve the right to suspend service at any time</li>
                <li>Location access depends on host business hours</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">7. Compliance</h2>
              <ul className="list-disc pl-6 text-gray-300 mt-2">
                <li>We comply with all KYC/AML requirements</li>
                <li>Transactions are monitored for suspicious activity</li>
                <li>We report as required by federal and state laws</li>
                <li>We cooperate with law enforcement investigations</li>
                <li>Users must comply with all applicable laws</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">8. Host Business Rights</h2>
              <ul className="list-disc pl-6 text-gray-300 mt-2">
                <li>Right to refuse service at their discretion</li>
                <li>Authority to enforce business hours and access policies</li>
                <li>Maintain safe and secure premises for BTM operation</li>
                <li>Receive commission as per separate host agreement</li>
                <li>Report suspicious activities to DiversiCoin</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">9. Dispute Resolution</h2>
              <ul className="list-disc pl-6 text-gray-300 mt-2">
                <li>Contact support within 24 hours of transaction issues</li>
                <li>Provide transaction ID and relevant documentation</li>
                <li>Initial response within 1 business day</li>
                <li>Resolution timeline of 5-7 business days</li>
                <li>Escalation process available for unresolved issues</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">10. Technical Specifications</h2>
              <ul className="list-disc pl-6 text-gray-300 mt-2">
                <li>BTM Hardware: Model V, Universal Model, or Mini Model</li>
                <li>Network Requirements: Minimum 10Mbps connection</li>
                <li>Transaction Timeout: 10 minutes</li>
                <li>Receipt Generation: Thermal printer, digital copy available</li>
                <li>Maximum Transaction Time: 5 minutes per session</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">11. Insurance and Indemnification</h2>
              <ul className="list-disc pl-6 text-gray-300 mt-2">
                <li>BTMs insured up to $1,000,000 per location</li>
                <li>Coverage for physical damage and theft</li>
                <li>User transactions covered against hardware malfunction</li>
                <li>No coverage for user input errors or wallet mistakes</li>
                <li>Host locations covered under separate policy</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">12. Legal Jurisdiction</h2>
              <ul className="list-disc pl-6 text-gray-300 mt-2">
                <li>Governed by Texas state law</li>
                <li>Exclusive jurisdiction in Dallas County, Texas</li>
                <li>Class action waiver in effect</li>
                <li>Arbitration required before litigation</li>
                <li>Small claims court option preserved</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">13. Force Majeure</h2>
              <ul className="list-disc pl-6 text-gray-300 mt-2">
                <li>Service interruption due to natural disasters</li>
                <li>Network outages beyond our control</li>
                <li>Government actions or regulations</li>
                <li>Blockchain network disruptions</li>
                <li>Civil unrest or other extraordinary events</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">14. Intellectual Property</h2>
              <ul className="list-disc pl-6 text-gray-300 mt-2">
                <li>DiversiCoin brand and logos protected</li>
                <li>BTM software and interface copyrighted</li>
                <li>Patents pending on BTM technology</li>
                <li>No reproduction without permission</li>
                <li>Limited license for normal BTM use only</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">15. Privacy Policy</h2>
              <p className="text-gray-300">
                Our Privacy Policy is incorporated into these Terms by reference. Please review our{' '}
                <Link to="/privacy-policy" className="text-purple-400 hover:text-purple-300">
                  Privacy Policy
                </Link>
                {' '}for information about how we collect, use, and protect your data.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">16. Liability</h2>
              <p className="text-gray-300">We are not responsible for:</p>
              <ul className="list-disc pl-6 text-gray-300 mt-2">
                <li>Bitcoin price volatility or market fluctuations</li>
                <li>Network delays or transaction failures</li>
                <li>Loss of access to personal Bitcoin wallets</li>
                <li>Incorrect wallet addresses provided by users</li>
                <li>Force majeure events affecting service</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">17. Termination</h2>
              <ul className="list-disc pl-6 text-gray-300 mt-2">
                <li>We may suspend service to any user at any time</li>
                <li>Violations may result in permanent ban</li>
                <li>Law enforcement will be notified of illegal activities</li>
                <li>Pending transactions may be cancelled upon termination</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">18. Changes to Terms</h2>
              <ul className="list-disc pl-6 text-gray-300 mt-2">
                <li>Terms may be updated at any time</li>
                <li>Changes will be posted on this page</li>
                <li>Continued use constitutes acceptance</li>
                <li>Users should review terms regularly</li>
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">19. Severability</h2>
              <p className="text-gray-300">
                If any provision of these Terms is found to be unenforceable or invalid, that provision 
                will be limited or eliminated to the minimum extent necessary, and the remaining provisions 
                will remain in full force and effect.
              </p>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-semibold text-white mb-4">20. Contact</h2>
              <p className="text-gray-300">
                For questions about these terms, contact us at:{' '}
                <a href="mailto:support@diversicoin.cash" className="text-purple-400 hover:text-purple-300">
                  support@diversicoin.cash
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}